import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import {
  BaseProduct,
  ProductPickerData,
  ProductPickerFilterType,
  ProductPickerProduct,
  ProductPickerSelectedProduct,
  ProductPickerState,
} from "./types";

const initialState: ProductPickerState = {
  products: [],
  selectedProducts: [],
  selectedProductsLoading: false,
  selectingAllProducts: false,
  search: "",
  filters: {
    product_type: [],
    vendor: [],
    tag: [],
    status: [],
    collection_id: [],
  },
};

const slice = createSlice({
  name: "productPicker",
  initialState,
  reducers: {
    openProductPicker: (draft, action: PayloadAction<ProductPickerData>) => {
      draft.data = action.payload;
      draft.filters = { ...initialState.filters, ...action.payload.filters };
    },
    closeProductPicker: () => {
      return initialState;
    },
    toggleProduct: (draft, action: PayloadAction<BaseProduct>) => {
      const product = action.payload;
      const index = draft.selectedProducts.findIndex(
        (p) => p.id === product.id
      );
      if (index > -1) {
        draft.selectedProducts.splice(index, 1);
      } else if (draft.data?.type === "gwp") {
        draft.selectedProducts = [product];
      } else {
        draft.selectedProducts.push(product);
      }
    },
    toggleProductVariant: (
      draft,
      action: PayloadAction<{
        product: BaseProduct;
        variantId: string;
        allVariantIds: string[];
      }>
    ) => {
      const { product, variantId, allVariantIds } = action.payload;

      // GWP only supports a single gift product, so dropping any previously
      // selected product enforces the one-product constraint while still
      // allowing multiple variants of that product to be selected.
      if (draft.data?.type === "gwp") {
        draft.selectedProducts = draft.selectedProducts.filter(
          (p) => p.id === product.id
        );
      }

      const productIndex = draft.selectedProducts.findIndex(
        (p) => p.id === product.id
      );

      if (productIndex === -1) {
        draft.selectedProducts.push({
          ...product,
          selectedVariantIds: [variantId],
          totalVariantCount: allVariantIds.length,
        });
      } else {
        const selectedProduct = draft.selectedProducts[productIndex];
        const selectedVariantIds = selectedProduct.selectedVariantIds;
        selectedProduct.totalVariantCount = allVariantIds.length;

        if (!selectedVariantIds) {
          selectedProduct.selectedVariantIds = allVariantIds.filter(
            (id) => id !== variantId
          );
        } else {
          if (selectedVariantIds.includes(variantId)) {
            selectedProduct.selectedVariantIds = selectedVariantIds.filter(
              (id) => id !== variantId
            );
            if (selectedProduct.selectedVariantIds.length === 0) {
              draft.selectedProducts.splice(productIndex, 1);
            }
          } else {
            selectedProduct.selectedVariantIds = [
              ...selectedVariantIds,
              variantId,
            ];
            if (
              selectedProduct.selectedVariantIds.length === allVariantIds.length
            ) {
              selectedProduct.selectedVariantIds = undefined;
            }
          }
        }
      }
    },
    setProductPickerSelectedProducts: (
      draft,
      action: PayloadAction<ProductPickerSelectedProduct[]>
    ) => {
      draft.selectedProducts = action.payload;
    },
    setProductPickerSearch: (draft, action: PayloadAction<string>) => {
      draft.search = action.payload;
    },
    setProductPickerFilter: (
      draft,
      action: PayloadAction<{
        filter: ProductPickerFilterType;
        value: string[];
      }>
    ) => {
      draft.filters[action.payload.filter] = action.payload.value;
    },
    deselectProduct: (draft, action: PayloadAction<string>) => {
      draft.selectedProducts = draft.selectedProducts.filter(
        (p) => p.id !== action.payload
      );
    },
    deselectProductList: (draft, action: PayloadAction<BaseProduct[]>) => {
      const productList = action.payload;
      draft.selectedProducts = draft.selectedProducts.filter(
        (p) => !productList.find((product) => product.id === p.id)
      );
    },
    setProductPickerSelectedProductsLoading: (
      draft,
      action: PayloadAction<boolean>
    ) => {
      draft.selectedProductsLoading = action.payload;
    },
    setProductPickerSelectingAllProducts: (
      draft,
      action: PayloadAction<boolean>
    ) => {
      draft.selectingAllProducts = action.payload;
    },
    setProductPickerProducts: (
      draft,
      action: PayloadAction<ProductPickerProduct[]>
    ) => {
      draft.products = action.payload;
    },
    setProductPickerProductCount: (
      draft,
      action: PayloadAction<number | undefined>
    ) => {
      draft.productCount = action.payload;
    },
  },
  selectors: {
    getProductPickerData: (state) => state.data,
    getProductPickerProducts: (state) => state.products,
    getProductPickerLoadedCount: (state) => state.products.length,
    getProductPickerProductCount: (state) => state.productCount,
    getProductPickerSelectedProducts: (state) => state.selectedProducts,
    getProductPickerSelectedCount: (state) => state.selectedProducts.length,
    areProductPickerSelectedProductsLoading: (state) =>
      state.selectedProductsLoading,
    isProductPickerSelectingAllProducts: (state) => state.selectingAllProducts,
    getProductPickerSearch: (state) => state.search,
    getProductPickerFilters: (state) => state.filters,
  },
});

export const {
  openProductPicker,
  closeProductPicker,
  setProductPickerSelectedProducts,
  setProductPickerFilter,
  toggleProduct,
  toggleProductVariant,
  deselectProductList,
  setProductPickerSearch,
  deselectProduct,
  setProductPickerSelectedProductsLoading,
  setProductPickerProducts,
  setProductPickerProductCount,
  setProductPickerSelectingAllProducts,
} = slice.actions;

export const productPickerReducer = slice.reducer;

export const {
  getProductPickerData,
  getProductPickerProducts,
  getProductPickerLoadedCount,
  getProductPickerProductCount,
  getProductPickerSelectedProducts,
  getProductPickerSearch,
  getProductPickerFilters,
  getProductPickerSelectedCount,
  areProductPickerSelectedProductsLoading,
  isProductPickerSelectingAllProducts,
} = slice.selectors;

const NO_EXCLUDED_PRODUCTS: string[] = [];

/** Product GIDs the open picker leaves out of its list (its opener's seed). */
export const getProductPickerExcludedProductIds = createSelector(
  getProductPickerData,
  (data) => data?.excludedProductIds ?? NO_EXCLUDED_PRODUCTS
);

export const getProductPickerSelectedIds = createSelector(
  getProductPickerSelectedProducts,
  (products) => products.map((p) => p.id)
);

export const getProductPickerSelectedVariantIds = createSelector(
  getProductPickerSelectedProducts,
  (products) => products.flatMap((p) => p.selectedVariantIds || [])
);

export const PRODUCT_PICKER_PAGE_SIZE = 150;
export const PRODUCT_PICKER_VARIANTS_PAGE_SIZE = 250;
export const CHECKOUT_BLOCK_UPSELL_MAX_PRODUCTS = 10;
